import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PublicPage, APP_NAME } from '@/components/public-page';
import {
  IS_HOOFDNAARHART,
  LOGIN_PATH,
  MARKETING_URL,
  supportEmail,
} from '@/lib/constants';

/**
 * The support address both app stores ask for in a listing.
 *
 * The address is editable in the admin settings (app_settings), so it can be
 * changed without a release -- which matters, because a store listing points
 * at this URL for the life of the app. VITE_SUPPORT_EMAIL is the fallback when
 * the setting is empty, as hnh_contact_email currently is.
 */
export default function SupportPage() {
  const [email, setEmail] = useState(supportEmail);

  useEffect(() => {
    const fetchEmail = async () => {
      const key = IS_HOOFDNAARHART ? 'hnh_contact_email' : 'contact_email';
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', key)
        .maybeSingle();
      if (data?.value) setEmail(data.value);
    };

    fetchEmail();
  }, []);

  return (
    <PublicPage
      title="Support"
      description={`Hulp en contact voor de ${APP_NAME} app: vragen over inloggen, je account of een uitnodigingscode.`}
    >
      <div className="space-y-6">
        <section>
          <h2 className="font-medium">Contact</h2>
          <p className="mt-1 text-muted-foreground">
            Vragen over de app, je account of een technisch probleem? Mail ons,
            we reageren meestal binnen een paar werkdagen.
          </p>
          {email && (
            <a
              href={`mailto:${email}`}
              className="mt-3 inline-flex items-center gap-2 font-medium text-green-700 underline underline-offset-4 hover:text-green-800"
            >
              <Mail className="h-4 w-4" />
              {email}
            </a>
          )}
        </section>

        <section>
          <h2 className="font-medium">Inloggen</h2>
          <p className="mt-1 text-muted-foreground">
            Heb je al een account? Ga naar{' '}
            <Link to={LOGIN_PATH} className="underline underline-offset-4">
              inloggen
            </Link>
            . Wachtwoord vergeten? Gebruik daar de link om een nieuw wachtwoord
            in te stellen.
          </p>
        </section>

        <section>
          {/* The question the login page cannot answer: registration needs an
              invite code, so an arrival without one has nowhere to go. */}
          <h2 className="font-medium">Nog geen uitnodigingscode?</h2>
          <p className="mt-1 text-muted-foreground">
            Registreren kan met een uitnodigingscode, die je krijgt via je
            organisatie of via een bijeenkomst. Op{' '}
            <a
              href={MARKETING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4"
            >
              hoofdnaarhart.nl
            </a>{' '}
            lees je wat we doen en kun je contact opnemen om aan te sluiten.
          </p>
        </section>

        <section>
          <h2 className="font-medium">Je gegevens</h2>
          <p className="mt-1 text-muted-foreground">
            In ons{' '}
            <Link to="/privacy" className="underline underline-offset-4">
              privacybeleid
            </Link>{' '}
            staat welke gegevens we bewaren en waarom. Wil je je account laten
            verwijderen, dan lees je op{' '}
            <Link to="/account-verwijderen" className="underline underline-offset-4">
              account verwijderen
            </Link>{' '}
            hoe dat werkt.
          </p>
        </section>
      </div>
    </PublicPage>
  );
}
